import * as React from 'react'
import { Skeleton, SkeletonProps } from '../Skeleton'
import { SkeletonSwitchContext } from './SkeletonSwitchProvider'

export interface SkeletonSwitchProps extends Omit<SkeletonProps, 'children'> {
  /**
   * loading state will toggle actual component to render and the skeleton being rendered
   */
  loading?: boolean
  /**
   * When child component wants to render multiple skeletons we can skip wrapping them in a skeleton element
   * otherwise it will render incorrectly - skeleton doesnt support wrapping another skeleton inside it
   */
  skipLoadingSkeleton?: boolean
  /**
   * The children component to render can take a function comoponent thats passed loading state to toggle component
   */
  children:
    | React.ReactElement
    | ((loading: boolean) => React.ReactElement | null)
    | null
}
/**
 * Skeleton Switch - Display a placeholder preview of your content before the data gets loaded to reduce load-time frustration.
 *
 */
export const SkeletonSwitch = React.forwardRef<
  HTMLDivElement,
  SkeletonSwitchProps
>((props, ref) => {
  const { loading, skipLoadingSkeleton = false, children, ...other } = props
  const { loading: _loading, animation } = React.useContext(
    SkeletonSwitchContext
  )
  const isLoading = Boolean(loading ?? _loading)
  const componentToRender = React.useMemo(() => {
    return typeof children === 'function' ? children(isLoading) : children
  }, [isLoading, children])

  if (isLoading && !skipLoadingSkeleton) {
    return (
      // @ts-ignore
      <Skeleton animation={animation} {...other} ref={ref}>
        {componentToRender}
      </Skeleton>
    )
  }
  return componentToRender
})
SkeletonSwitch.displayName = 'SkeletonSwitch'
